import React, { useEffect, useState } from 'react';
import type { AppContext } from '../app';
import { getNewsHeadlinesManager } from '../news/NewsHeadlinesManager';
import type { NewsHeadlineRecord } from '../storage/schema';

interface Props {
  ctx: AppContext;
}

/**
 * NewsTicker — Phase 69.A horizontal scrolling marquee.
 *
 * Sits at the very top of AppShell. ~28px tall on desktop, smaller on
 * mobile. Renders the most recent N active (non-dismissed) headlines
 * scrolling right-to-left. Tap the ticker to open the news-paper
 * surface; tap the × on a headline to dismiss it from the ticker.
 *
 * Hidden entirely when there are no active headlines, so empty state
 * doesn't steal viewport on devices that have never seen 69.B's
 * RSS aggregator + have no operator-curated headlines yet.
 */
export function NewsTicker({ ctx }: Props): JSX.Element | null {
  const manager = getNewsHeadlinesManager(ctx);
  const [rows, setRows] = useState<readonly NewsHeadlineRecord[]>([]);

  useEffect(() => manager.onActiveChange(setRows), [manager]);

  if (rows.length === 0) return null;

  // We render the headline list twice so the CSS marquee can loop
  // seamlessly. Each item is keyed by id+pass so React doesn't
  // complain about duplicate keys.
  const passes: Array<{ row: NewsHeadlineRecord; pass: 'a' | 'b' }> = [
    ...rows.map((row) => ({ row, pass: 'a' as const })),
    ...rows.map((row) => ({ row, pass: 'b' as const })),
  ];

  const openPaper = () => ctx.director.bridge('news-paper', 'open news ticker');

  return (
    <div
      className="news-ticker"
      data-testid="news-ticker"
      role="region"
      aria-label="local news headlines"
    >
      <button
        className="news-ticker-label"
        onClick={openPaper}
        data-testid="news-ticker-open"
        aria-label="open news paper"
      >
        ▣ news
      </button>
      <div
        className="news-ticker-marquee"
        onClick={openPaper}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPaper(); }}
      >
        <div className="news-ticker-track" style={{ animationDuration: `${Math.max(20, rows.length * 9)}s` }}>
          {passes.map(({ row, pass }, i) => (
            <span
              key={`${row.id}-${pass}-${i}`}
              className="news-ticker-item"
              data-headline-id={row.id}
            >
              <span className="news-ticker-source">{row.source}</span>
              <span className="news-ticker-sep">·</span>
              <span className="news-ticker-title">{row.title}</span>
            </span>
          ))}
        </div>
      </div>
      <button
        className="news-ticker-dismiss"
        data-testid="news-ticker-dismiss-first"
        title="dismiss leading headline"
        onClick={(e) => {
          e.stopPropagation();
          const first = rows[0];
          if (first !== undefined) void manager.dismiss(first.id);
        }}
      >
        ×
      </button>
      <style>{tickerStyles}</style>
    </div>
  );
}

const tickerStyles = `
.news-ticker {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 8px;
  height: 28px;
  padding: 0 8px;
  background: var(--bg-2);
  border-bottom: 1px solid var(--rule);
  overflow: hidden;
  flex-shrink: 0;
}
.news-ticker-label {
  flex-shrink: 0;
  background: none;
  border: 0;
  color: var(--phos);
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0 4px;
}
.news-ticker-label:hover { color: var(--ink); }
.news-ticker-marquee {
  flex: 1;
  overflow: hidden;
  position: relative;
  cursor: pointer;
}
.news-ticker-track {
  display: inline-flex;
  white-space: nowrap;
  animation: news-ticker-scroll linear infinite;
  will-change: transform;
}
.news-ticker-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 36px;
  font-size: 12px;
  color: var(--ink);
  line-height: 28px;
}
.news-ticker-source {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--phos);
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.news-ticker-sep { color: var(--ink-dim); }
.news-ticker-title { font-family: var(--serif, var(--font-serif, serif)); }
.news-ticker-dismiss {
  flex-shrink: 0;
  background: none;
  border: 0;
  color: var(--ink-dim);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.news-ticker-dismiss:hover { color: var(--amber); }
@keyframes news-ticker-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
  .news-ticker-track { animation: none; }
}
@media (max-width: 600px) {
  .news-ticker { height: 24px; }
  .news-ticker-label { font-size: 9px; }
  .news-ticker-item { font-size: 11px; line-height: 24px; margin-right: 24px; }
}
`;
